import React, { useState } from "react";
import { Wand2, Image, Layers, Loader2, Plus, X, Link as LinkIcon } from "lucide-react";
import { useDelayedUnmount } from "../hooks/useDelayedUnmount";

const MAX_EXTRA_REFERENCE_IMAGES = 2;

const WanxEditPanel = ({
  feature,
  onFeatureChange,
  prompt,
  onPromptChange,
  refImages,
  onAddRefImage,
  onRemoveRefImage,
  onReferenceLimitReached,
  size,
  onSizeChange,
  isProcessing,
  onSubmit,
  onOpenResourceSelector,
  theme,
}) => {
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const handleAddUrl = () => {
    if (!urlInput.trim()) return;
    if (refImages.length >= MAX_EXTRA_REFERENCE_IMAGES) {
      onReferenceLimitReached?.();
      return;
    }
    const added = onAddRefImage({ url: urlInput.trim(), name: "URL图片" });
    if (added === false) return;
    setUrlInput("");
    setShowUrlInput(false);
  };

  const features = [
    { id: "edit", label: "图像编辑", icon: Image },
    { id: "multiref", label: "多图参考", icon: Layers },
  ];

  const getFeatureDescription = () => {
    switch (feature) {
      case "edit":
        return "当前图片自动作为图1，最多再导入2张参考图；可在提示词中用“图1、图2、图3”引用";
      case "multiref":
        return "当前图片计入参考图总数，最多使用3张图片生成新画面";
      default:
        return "";
    }
  };

  const showRefImages = feature === "edit" || feature === "multiref";
  const refImagesSec = useDelayedUnmount(showRefImages, 150);
  const urlInputSec = useDelayedUnmount(showUrlInput, 150);

  return (
    <div className="space-y-6">
      {/* 功能切换 Tab */}
      <div className="space-y-3">
        <h3 className="text-sm font-bold flex items-center gap-2 uppercase tracking-wider" style={{ color: 'var(--color-text-primary)' }}>
          <Wand2 size={16} style={{ color: theme.colors.accentPrimary }} /> 千问图像3.0 Pro
        </h3>
        <div className="flex rounded-lg p-1 border" style={{ backgroundColor: 'var(--color-bg-overlay)', borderColor: 'var(--color-border-primary)' }}>
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <button
                key={f.id}
                onClick={() => onFeatureChange(f.id)}
                className="flex-1 px-3 py-2 rounded-md text-xs font-bold flex items-center justify-center gap-2 transition-all"
                style={{
                  backgroundColor: feature === f.id ? theme.colors.accentPrimary : 'transparent',
                  color: feature === f.id ? 'white' : 'var(--color-text-muted)'
                }}
              >
                <Icon size={14} /> {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 功能说明 */}
      <div className="rounded-xl p-4 border" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-primary)' }}>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
          {getFeatureDescription()}
        </p>
      </div>

      {/* 参考图片区（edit/multiref） */}
      {refImagesSec.mounted && (
        <div className={`space-y-3 ${refImagesSec.exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`}>
          <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
            附加参考图 ({refImages.length}/{MAX_EXTRA_REFERENCE_IMAGES})
          </h4>
          <div className="grid grid-cols-3 gap-2">
            {refImages.map((img, idx) => (
              <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border" style={{ borderColor: 'var(--color-border-primary)' }}>
                <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[var(--overlay-medium)] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button
                    onClick={() => onRemoveRefImage(idx)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded-full transition-all"
                    style={{ backgroundColor: theme.colors.redPrimary, color: 'white' }}
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 px-2 py-1 text-2xs truncate" style={{ backgroundColor: 'var(--overlay-heavy)', color: 'white' }}>
                  {img.name}
                </div>
              </div>
            ))}
            <button
              onClick={onOpenResourceSelector}
              className="aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all hover:border-solid"
              style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
            >
              <Plus size={20} />
              <span className="text-2xs">{refImages.length >= MAX_EXTRA_REFERENCE_IMAGES ? "已满" : "添加"}</span>
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onOpenResourceSelector}
              className="flex-1 py-2 rounded-lg text-xs font-bold transition-all border"
              style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
            >
              从资源中心导入
            </button>
            <button
              onClick={() => {
                if (refImages.length >= MAX_EXTRA_REFERENCE_IMAGES) {
                  onReferenceLimitReached?.();
                  return;
                }
                setShowUrlInput(!showUrlInput);
              }}
              className="flex-1 py-2 rounded-lg text-xs font-bold transition-all border flex items-center justify-center gap-2"
              style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
            >
              <LinkIcon size={14} /> 输入URL
            </button>
          </div>
          {urlInputSec.mounted && (
            <div className={`flex gap-2 ${urlInputSec.exiting ? "animate-out fade-out slide-out-to-top-2 duration-150" : "animate-in fade-in slide-in-from-top-2 duration-200"}`}>
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://..."
                className="flex-1 px-3 py-2 rounded-lg text-xs outline-none"
                style={{
                  backgroundColor: 'var(--color-bg-overlay)',
                  border: '1px solid var(--color-border-primary)',
                  color: 'var(--color-text-primary)'
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleAddUrl()}
              />
              <button
                onClick={handleAddUrl}
                className="px-4 py-2 rounded-lg text-xs font-bold"
                style={{ backgroundColor: theme.colors.bluePrimary, color: 'white' }}
              >
                添加
              </button>
            </div>
          )}
        </div>
      )}

      {/* 输出设置 */}
      <div className="space-y-3">
        <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
          输出设置
        </h4>
        <div className="flex gap-2">
          {[
            { value: "auto", label: "自动" },
            { value: "1K", label: "1K" },
            { value: "2K", label: "2K" },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => onSizeChange(option.value)}
              className="flex-1 py-2 rounded-lg text-xs font-bold transition-all border"
              style={{
                backgroundColor: size === option.value ? theme.colors.bluePrimary : 'transparent',
                borderColor: size === option.value ? theme.colors.bluePrimary : 'var(--color-border-primary)',
                color: size === option.value ? 'white' : 'var(--color-text-muted)'
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* 提示词 */}
      <div className="space-y-3">
        <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
          提示词
        </h4>
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder={
            feature === "edit"
              ? "例如：把图2的涂鸦喷绘在图1的汽车上"
              : "例如：参考这些角色和场景，生成一个森林冒险场景"
          }
          className="w-full h-32 rounded-lg p-3 text-sm outline-none resize-none"
          style={{
            backgroundColor: 'var(--color-bg-overlay)',
            border: '1px solid var(--color-border-primary)',
            color: 'var(--color-text-primary)'
          }}
        />
      </div>

      {/* 提交按钮 */}
      <button
        onClick={onSubmit}
        disabled={isProcessing || !prompt}
        className="w-full py-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 transition-all"
        style={{
          background: `linear-gradient(to right, ${theme.colors.accentPrimary}, ${theme.colors.bluePrimary})`,
          color: 'white'
        }}
      >
        {isProcessing ? (
          <>
            <Loader2 className="animate-spin" size={16} />
            生成中...
          </>
        ) : (
          <>
            <Wand2 size={16} />
            开始生成
          </>
        )}
      </button>
    </div>
  );
};

export default WanxEditPanel;
